// src/api/index.js - VERSIÓN CORREGIDA

// ==================== FUNCIONES DE CORRECCIÓN ====================

/**
 * Convierte URLs HTTP a HTTPS en producción
 */
const FORZAR_HTTPS = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  // En producción, convertir http:// a https://
  if (typeof window !== 'undefined' && window.location &&
      window.location.hostname !== 'localhost' && 
      window.location.hostname !== '127.0.0.1') {
    return url.replace('http://', 'https://');
  }
  
  return url;
};

/**
 * Limpia una URL de imagen, extrayendo solo el nombre del archivo
 */
const LIMPIAR_URL_IMAGEN = (urlImagen) => {
  if (!urlImagen) return null;
  
  // Si es placeholder o data URL, devolver tal cual
  if (urlImagen.includes('placeholder') || urlImagen.startsWith('data:')) {
    return urlImagen;
  }
  
  // Extraer solo el nombre del archivo
  const partes = urlImagen.split('/');
  const nombreArchivo = partes[partes.length - 1];
  
  // Si no parece un archivo de imagen, devolver null
  if (!nombreArchivo || !nombreArchivo.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)) {
    return null;
  }
  
  return nombreArchivo;
};

/**
 * Verifica si estamos en producción
 */
const ES_PRODUCCION = () => {
  if (process.env.VUE_APP_ENV === 'development') return false;
  if (process.env.VUE_APP_ENV === 'production') return true;
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname !== 'localhost' && 
           window.location.hostname !== '127.0.0.1';
  }
  return process.env.NODE_ENV === 'production';
};

/**
 * Verifica si una URL es local (contiene IPs o localhost)
 */
const ES_URL_LOCAL = (url) => {
  return url.includes('localhost') || 
         url.includes('192.168.') || 
         url.includes('127.0.0.1') ||
         url.includes(':5090') ||
         url.includes(':5059');
};

// ==================== CONFIGURACIÓN DE VARIABLES DE ENTORNO ====================

const LOCAL_PORT = process.env.VUE_APP_API_PORT || '5090';
const LOCAL_BASE = process.env.VUE_APP_BASE_URL || `http://localhost:${LOCAL_PORT}`;
const LOCAL_API = process.env.VUE_APP_API_URL || `${LOCAL_BASE}/api`;

const PROD_BASE = process.env.VUE_APP_PROD_BASE_URL || 'https://api.jeaninformatico.com';
const PROD_API = process.env.VUE_APP_PROD_API_URL || `${PROD_BASE}/api`;

const CONFIG = {
  servers: [
    {
      id: 'localhost',
      name: 'Desarrollo Local',
      baseUrl: LOCAL_BASE,
      apiUrl: LOCAL_API,
      isDefault: true
    },
    {
      id: 'produccion',
      name: 'Producción',
      baseUrl: PROD_BASE,
      apiUrl: PROD_API,
      isDefault: false
    }
  ],
  
  // Modo actual: 'auto', 'local', 'produccion'
  mode: process.env.VUE_APP_ENV === 'development' ? 'local' : 'auto',
  
  // Configuración actual
  currentServer: null,
  
  // Configuración por defecto
  getDefaultServer() {
    if (ES_PRODUCCION()) {
      return this.servers.find(s => s.id === 'produccion') || this.servers[0];
    }
    return this.servers.find(s => s.id === 'localhost') || this.servers[0];
  }
};

// Clave para almacenar en localStorage
const STORAGE_KEY = 'api_server_config';

// Flag para modo offline
let offlineMode = false;

// Cache de respuestas
const responseCache = new Map();
const CACHE_TTL = 30000; // 30 segundos

// ==================== FUNCIONES DEL SERVIDOR ====================

// Función para probar servidor
const testServer = async (server) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);
  
  try {
    console.log(`🔍 Probando: ${server.name} (${server.baseUrl})`);
    
    const response = await fetch(`${server.apiUrl}/Producto?pageSize=1`, {
      method: 'GET',
      signal: controller.signal,
      headers: { 
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      mode: 'cors',
      cache: 'no-cache'
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok) {
      console.log(`✅ ${server.name} disponible`);
      return { ...server, available: true };
    }
    
    try {
      const healthResponse = await fetch(`${server.baseUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
        mode: 'cors'
      });
      
      clearTimeout(timeoutId);
      
      if (healthResponse.ok) {
        console.log(`✅ ${server.name} disponible (health check)`);
        return { ...server, available: true };
      }
    } catch (healthError) {
      console.log(`⚠️ Health check falló: ${healthError.message}`);
    }
    
    console.log(`❌ ${server.name} no disponible`);
    return { ...server, available: false };
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.log(`⏱️ ${server.name} timeout`);
    } else {
      console.log(`❌ ${server.name} error: ${error.message}`);
    }
    
    return { ...server, available: false };
  }
};

// ==================== INICIALIZACIÓN ====================

// Inicializar la API
export const initializeAPI = async (forceRefresh = false) => {
  console.log('🔄 Inicializando API...');
  const esProd = ES_PRODUCCION();
  console.log('🌍 Entorno detectado:', esProd ? 'PRODUCCIÓN' : 'LOCAL');
  console.log('⚙️ Variables de entorno backend:', {
    VUE_APP_BASE_URL: process.env.VUE_APP_BASE_URL,
    VUE_APP_API_URL: process.env.VUE_APP_API_URL,
    LOCAL_BASE,
    LOCAL_API
  });

  // Si estamos en entorno local, limpiar cualquier servidor de producción residual en localStorage
  const savedServerRaw = localStorage.getItem(STORAGE_KEY);
  if (savedServerRaw) {
    try {
      const savedParsed = JSON.parse(savedServerRaw);
      if (!esProd && (savedParsed.id === 'produccion' || (savedParsed.baseUrl && savedParsed.baseUrl.includes('jeaninformatico.com')))) {
        console.warn('🧹 Eliminando servidor de producción guardado en localStorage para forzar entorno LOCAL');
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  
  if (!forceRefresh && CONFIG.currentServer) {
    if (!esProd && CONFIG.currentServer.id === 'produccion') {
      console.warn('🔄 currentServer era producción en local. Re-evaluando hacia local...');
    } else {
      return CONFIG.currentServer;
    }
  }
  
  if (offlineMode) {
    console.log('📱 Modo offline activo');
    const defaultServer = CONFIG.getDefaultServer();
    CONFIG.currentServer = defaultServer;
    return defaultServer;
  }

  // EN ENTORNO LOCAL: Probar primero el servidor local configurado en las variables de entorno
  if (!esProd) {
    const localServer = CONFIG.servers.find(s => s.id === 'localhost');
    console.log('🔌 Conectando a servidor local:', localServer.apiUrl);
    const localTest = await testServer(localServer);
    if (localTest.available) {
      CONFIG.currentServer = localTest;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(localTest));
      offlineMode = false;
      console.log(`✅ Servidor local conectado exitosamente: ${localTest.apiUrl}`);
      return localTest;
    }
    console.warn(`⚠️ Servidor local no respondió en ${localServer.baseUrl}. Verificando alternativas...`);
  }
  
  // Verificar servidor guardado
  const savedServer = localStorage.getItem(STORAGE_KEY);
  
  if (savedServer && !forceRefresh) {
    try {
      const parsed = JSON.parse(savedServer);
      if (!esProd && parsed.id === 'produccion') {
        console.log('⏩ Omitiendo servidor de producción guardado porque estamos en modo local');
      } else {
        const testResult = await testServer(parsed);
        
        if (testResult.available) {
          CONFIG.currentServer = parsed;
          offlineMode = false;
          console.log(`✅ Usando servidor guardado: ${parsed.name}`);
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Error al cargar servidor guardado:', error);
    }
  }
  
  const defaultServer = CONFIG.getDefaultServer();
  
  if (CONFIG.mode === 'local') {
    const localServer = CONFIG.servers.find(s => s.id === 'localhost');
    CONFIG.currentServer = localServer;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(localServer));
    return localServer;
  }
  
  if (CONFIG.mode === 'produccion') {
    const prodServer = CONFIG.servers.find(s => s.id === 'produccion');
    CONFIG.currentServer = prodServer;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prodServer));
    return prodServer;
  }
  
  console.log('🔍 Modo auto: detectando entorno...');
  
  const defaultTest = await testServer(defaultServer);
  if (defaultTest.available) {
    CONFIG.currentServer = defaultTest;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultTest));
    offlineMode = false;
    console.log(`✅ Servidor por defecto disponible: ${defaultTest.name}`);
    return defaultTest;
  }
  
  const otherServer = CONFIG.servers.find(s => s.id !== defaultServer.id);
  const otherTest = await testServer(otherServer);
  
  if (otherTest.available) {
    CONFIG.currentServer = otherTest;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(otherTest));
    offlineMode = false;
    console.log(`✅ Servidor alternativo disponible: ${otherTest.name}`);
    return otherTest;
  }
  
  console.warn('⚠️ No hay servidores disponibles, modo offline');
  offlineMode = true;
  
  CONFIG.currentServer = defaultServer;
  
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ 
    ...defaultServer, 
    offline: true 
  }));
  
  return defaultServer;
};

// ==================== CACHE ====================

// Limpiar cache
export const clearCache = () => {
  responseCache.clear();
  console.log('🧹 Cache limpiado');
};

// ==================== API FETCH ====================

// Función principal apiFetch
export const apiFetch = async (endpoint, options = {}) => {
  const cacheKey = `${endpoint}-${JSON.stringify(options)}`;
  
  if ((!options.method || options.method === 'GET') && responseCache.has(cacheKey)) {
    const cached = responseCache.get(cacheKey);
    if (Date.now() - cached.timestamp < CACHE_TTL) {
      console.log(`📦 Cache: ${endpoint}`);
      return cached.data;
    }
    responseCache.delete(cacheKey);
  }
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  
  try {
    if (offlineMode) {
      if (Math.random() < 0.2) {
        initializeAPI().catch(() => {});
      }
      throw new Error('offline');
    }
    
    if (!CONFIG.currentServer) {
      await initializeAPI();
      if (offlineMode) {
        throw new Error('offline');
      }
    }
    
    if (!CONFIG.currentServer?.apiUrl) {
      throw new Error('Configuración inválida');
    }
    
    const url = `${CONFIG.currentServer.apiUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    };
    
    const config = {
      method: options.method || 'GET',
      headers,
      signal: controller.signal,
      cache: 'no-cache'
    };
    
    if (options.body) {
      config.body = typeof options.body === 'string' 
        ? options.body 
        : JSON.stringify(options.body);
    }
    
    console.log(`📡 ${config.method} ${url}`);
    
    const response = await fetch(url, config);
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = `Error ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.title || errorMessage;
      } catch {
        try {
          errorMessage = await response.text() || errorMessage;
        } catch {
          // Ignorar error
        }
      }
      throw new Error(errorMessage);
    }
    
    if (response.status === 204) {
      return { success: true };
    }
    
    const data = await response.json().catch(() => ({ 
      success: true, 
      status: response.status 
    }));
    
    if (!options.method || options.method === 'GET') {
      responseCache.set(cacheKey, { data, timestamp: Date.now() });
      try {
        localStorage.setItem(`fallback_${endpoint}`, JSON.stringify(data));
      } catch {
        // Ignorar error
      }
    }
    
    return data;
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.error('⏱️ Timeout:', endpoint);
      
      const timeoutCount = Number(localStorage.getItem('timeout_count') || 0);
      localStorage.setItem('timeout_count', String(timeoutCount + 1));
      
      if (timeoutCount > 3) {
        offlineMode = true;
      }
      
      throw new Error('Tiempo de espera agotado');
    }
    
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('NetworkError') ||
        error.name === 'TypeError' ||
        error.message === 'offline') {
      
      console.warn('🔁 Error de red, intentando datos de respaldo...');
      
      const fallbackData = localStorage.getItem(`fallback_${endpoint}`);
      if (fallbackData) {
        try {
          console.log('📦 Usando datos de respaldo local');
          return JSON.parse(fallbackData);
        } catch {
          // Ignorar error
        }
      }
      
      if (!options.method || options.method === 'GET') {
        if (endpoint.includes('/Producto')) return [];
        if (endpoint.includes('/Categoria')) return [];
      }
      
      throw new Error('Sin conexión al servidor');
    }
    
    throw error;
  }
};

// ==================== FUNCIONES DE URL ====================

export const getCurrentBaseUrl = () => {
  return CONFIG.currentServer?.baseUrl || LOCAL_BASE;
};

export const getCurrentApiUrl = () => {
  return CONFIG.currentServer?.apiUrl || LOCAL_API;
};

/**
 * Obtiene URL completa de un recurso
 * Corrige automáticamente HTTP → HTTPS en producción
 * Corrige URLs locales en producción
 */
export const getResourceUrl = (path) => {
  if (!path) return null;
  
  // 1. Corregir HTTP → HTTPS en producción
  path = FORZAR_HTTPS(path);
  
  // 2. Si ya es URL completa
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Si estamos en producción y la URL es local, corregirla
    if (ES_PRODUCCION() && ES_URL_LOCAL(path)) {
      const nombreArchivo = LIMPIAR_URL_IMAGEN(path);
      if (nombreArchivo) {
        const baseUrl = getCurrentBaseUrl();
        const nuevaUrl = `${baseUrl}/imagenes/producto/${nombreArchivo}`;
        console.log('🔄 Corrigiendo URL recurso:', path, '→', nuevaUrl);
        return nuevaUrl;
      }
    }
    return path;
  }
  
  // 3. Si es data URL
  if (path.startsWith('data:')) return path;
  
  // 4. Si es ruta relativa
  const baseUrl = getCurrentBaseUrl();
  if (path.startsWith('/')) return `${baseUrl}${path}`;
  return `${baseUrl}/imagenes/${path}`;
};

/**
 * Procesa una URL de imagen para usar HTTPS en producción
 */
export const procesarUrlImagen = (url) => {
  if (!url) return null;
  
  // Si estamos en producción y la URL es HTTP, convertir a HTTPS
  if (ES_PRODUCCION() && url.startsWith('http://')) {
    url = url.replace('http://', 'https://');
  }
  
  return url;
};

/**
 * Procesa una lista de imágenes (como la que devuelve el endpoint /api/Imagen/listar)
 */
export const procesarListaImagenes = (imagenes) => {
  if (!Array.isArray(imagenes)) return [];
  
  return imagenes.map(imagen => ({
    ...imagen,
    url: procesarUrlImagen(imagen.url),
    urlCompleta: procesarUrlImagen(imagen.urlCompleta || imagen.url)
  }));
};

/**
 * Obtiene URL de imagen corregida
 * - Convierte HTTP → HTTPS en producción
 * - Reemplaza URLs locales por el dominio correcto
 * - Extrae nombre de archivo de URLs absolutas
 */
export const getImageUrl = (imagePath) => {
  // Sin imagen
  if (!imagePath) return 'https://via.placeholder.com/150?text=Sin+Imagen';
  
  // Modo offline
  if (offlineMode) return 'https://via.placeholder.com/150?text=Offline';
  
  // 1. CORRECCIÓN: Forzar HTTPS en producción
  if (ES_PRODUCCION()) {
    // Si es HTTP, convertirlo a HTTPS
    if (imagePath.startsWith('http://')) {
      imagePath = imagePath.replace('http://', 'https://');
    }
    
    // Si la URL contiene el dominio pero es HTTP
    if (imagePath.includes('api.jeaninformatico.com') && imagePath.startsWith('http://')) {
      imagePath = imagePath.replace('http://', 'https://');
    }
  }
  
  // 2. Si ya es URL completa (http o https)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // 3. Si es ruta relativa
  const baseUrl = getCurrentBaseUrl();
  
  if (imagePath.startsWith('/imagenes/')) {
    return `${baseUrl}${imagePath}`;
  }
  
  if (imagePath.startsWith('/')) {
    return `${baseUrl}${imagePath}`;
  }
  
  // 4. Si es solo nombre de archivo
  return `${baseUrl}/imagenes/producto/${imagePath}`;
};

// ==================== UPLOAD ====================

export const uploadImage = async (file, endpoint = '/Imagenes/subir') => {
  if (offlineMode) {
    throw new Error('No se puede subir en modo offline');
  }
  
  if (!CONFIG.currentServer) {
    await initializeAPI();
  }
  
  const formData = new FormData();
  formData.append('imagen', file);
  
  return apiFetch(endpoint, {
    method: 'POST',
    body: formData,
    headers: { 'Accept': 'application/json' }
  });
};

// ==================== GETTERS Y SETTERS ====================

export const getCurrentServer = () => CONFIG.currentServer;
export const isOffline = () => offlineMode;

export const setServer = async (serverId) => {
  const server = CONFIG.servers.find(s => s.id === serverId);
  if (!server) throw new Error(`Servidor ${serverId} no encontrado`);
  
  const testResult = await testServer(server);
  
  if (testResult.available) {
    CONFIG.currentServer = testResult;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testResult));
    offlineMode = false;
    localStorage.setItem('timeout_count', '0');
    console.log(`✅ Servidor cambiado a: ${testResult.name}`);
    return testResult;
  }
  
  throw new Error(`Servidor ${server.name} no disponible`);
};

// Cambiar entre modos
export const setMode = async (mode) => {
  if (!['auto', 'local', 'produccion'].includes(mode)) {
    throw new Error('Modo inválido. Use: auto, local, produccion');
  }
  
  CONFIG.mode = mode;
  localStorage.setItem('api_mode', mode);
  
  CONFIG.currentServer = null;
  offlineMode = false;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.setItem('timeout_count', '0');
  
  return await initializeAPI(true);
};

export const getMode = () => CONFIG.mode;

export const checkStatus = async () => {
  try {
    if (offlineMode) {
      return { 
        server: CONFIG.currentServer, 
        available: false, 
        offline: true,
        mode: CONFIG.mode
      };
    }
    
    if (!CONFIG.currentServer) {
      await initializeAPI();
    }
    
    const testResult = await testServer(CONFIG.currentServer);
    
    return {
      server: CONFIG.currentServer,
      available: testResult.available,
      offline: offlineMode,
      mode: CONFIG.mode
    };
    
  } catch (error) {
    return {
      server: CONFIG.currentServer,
      available: false,
      offline: offlineMode,
      mode: CONFIG.mode,
      error: error.message
    };
  }
};

export const forceOfflineMode = () => {
  offlineMode = true;
  console.log('📱 Modo offline forzado');
  return true;
};

export const disableOfflineMode = async () => {
  offlineMode = false;
  CONFIG.currentServer = null;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.setItem('timeout_count', '0');
  
  try {
    await initializeAPI(true);
    return true;
  } catch {
    return false;
  }
};

// ==================== INICIALIZACIÓN AUTOMÁTICA ====================

if (typeof window !== 'undefined') {
  const savedMode = localStorage.getItem('api_mode');
  if (savedMode && ['auto', 'local', 'produccion'].includes(savedMode)) {
    CONFIG.mode = savedMode;
  }
  
  console.log('🚀 Iniciando aplicación...');
  console.log('🌍 Entorno:', ES_PRODUCCION() ? 'PRODUCCIÓN' : 'DESARROLLO LOCAL');
  
  setTimeout(() => {
    initializeAPI().catch(() => {
      console.warn('⚠️ No se pudo conectar, modo offline');
      offlineMode = true;
    });
  }, 500);
  
  window.addEventListener('online', () => {
    console.log('🌐 Conexión restaurada');
    disableOfflineMode();
  });
  
  window.addEventListener('offline', () => {
    console.log('📱 Sin conexión');
    forceOfflineMode();
  });
}

// ==================== EXPORTACIONES ====================

export default {
  initializeAPI,
  apiFetch,
  getCurrentBaseUrl,
  getCurrentApiUrl,
  getResourceUrl,
  getImageUrl,
  procesarUrlImagen,
  procesarListaImagenes,
  uploadImage,
  getCurrentServer,
  setServer,
  setMode,
  getMode,
  checkStatus,
  isOffline,
  forceOfflineMode,
  disableOfflineMode,
  clearCache,
  config: CONFIG,
  ES_PRODUCCION
};